/**
 * Action validation logic
 */

import type { GameState, ActionType, PlayerAction, ValidationResult } from './types'

/**
 * Get available actions for a player
 * @param state - Current game state
 * @param playerId - Player ID
 * @returns Array of legal actions
 */
export function getAvailableActions(state: GameState, playerId: string): ActionType[] {
  const player = state.players.find((p) => p.id === playerId)
  if (!player) {
    return []
  }

  // Player must be active to act
  if (player.state !== 'active') {
    return []
  }

  // Not this player's turn
  if (state.currentPlayerId !== playerId) {
    return []
  }

  const actions: ActionType[] = []

  // Get highest current bet (excluding this player's own bet)
  // Preflop: minimum call is the full BB amount even if BB posted less (partial blind).
  // Only applies when blinds have actually been posted (at least one non-zero bet).
  const otherPlayers = state.players.filter((p) => p.id !== playerId)
  const blindsPosted = state.boardState === 'deal' && state.players.some((p) => p.currentBet > 0)
  const preflopMinBet = blindsPosted ? state.bigBlind : 0
  const highestBet = Math.max(preflopMinBet, ...otherPlayers.map((p) => p.currentBet))
  const otherPlayersActiveHaveChips = otherPlayers.some((p) => p.chips > 0 && p.state === 'active')

  // Check if there's a bet to call
  const betToCall = highestBet - player.currentBet

  // Raise is available if:
  // 1. There's a bet to call and player has enough for a min raise, OR
  // 2. Player is in check position (betToCall <= 0) and player has enough chips to raise
  //    This handles cases like big blind raising after calls, or when player has already
  //    bet more than others (negative betToCall)
  if (
    (betToCall > 0 && player.chips > betToCall) ||
    (betToCall <= 0 && player.chips > state.bigBlind && state.boardState === 'deal')
  ) {
    // TODO: REVIEW: Can only raise or bet if other player have chips
    // In heads-up when opponent is all-in: raising is strategically pointless but still legal
    // Is going to be disable in this poker server to simply action in a real video game
    if (otherPlayersActiveHaveChips) {
      actions.push('raise')
    }
  }

  // Bet is available if no one has bet yet and player has chips
  if (highestBet === 0 && player.chips > 0 && !actions.includes('raise')) {
    actions.push('bet')
  }

  // Call is available if there's a bet to call and player has enough chips
  if (betToCall > 0 && player.chips >= betToCall) {
    actions.push('call')
  }

  // Check is available if no bet to call OR if player has already bet more than others
  // betToCall <= 0 means player's bet is >= highest bet from others
  if (betToCall <= 0) {
    actions.push('check')
  }
  const cantCall = !(betToCall > 0 && player.chips >= betToCall)
  // All-in is always available if player has chips and other
  const otherPlayersHaveChipsOrAllIn = otherPlayers.some(
    (p) => (p.state === 'active' && p.chips > 0) || (p.state === 'allin' && cantCall)
  )
  if (player.chips > 0 && otherPlayersHaveChipsOrAllIn) {
    actions.push('allin')
  }

  // Fold is always available (unless betToCall <= 0)
  // If betToCall is negative, player has already bet more than others (no need to fold)
  if (betToCall > 0) {
    actions.push('fold')
  }

  return actions
}

/**
 * Validate if an action is legal
 * @param state - Current game state
 * @param playerId - Player ID
 * @param action - Player action (discriminated union)
 * @returns Validation result
 */
export function validateAction(state: GameState, playerId: string, action: PlayerAction): ValidationResult {
  const player = state.players.find((p) => p.id === playerId)

  if (!player) {
    return { valid: false, error: 'Player not found' }
  }

  // Player must be active
  if (player.state !== 'active') {
    return { valid: false, error: `Player state is ${player.state}` }
  }

  // Must be player's turn
  if (state.currentPlayerId !== playerId) {
    return { valid: false, error: 'Not your turn' }
  }

  const availableActions = getAvailableActions(state, playerId)

  if (!availableActions.includes(action.type)) {
    return {
      valid: false,
      error: `Action ${action.type} not available. Available: ${availableActions.join(', ')}`
    }
  }

  // Validate amount for bet/raise using type narrowing
  if (action.type === 'bet' || action.type === 'raise') {
    if (action.amount <= 0) {
      return { valid: false, error: `${action.type} requires a positive amount` }
    }

    if (action.amount > player.chips) {
      return { valid: false, error: `Insufficient chips (have ${player.chips}, need ${action.amount})` }
    }

    // Minimum bet is the big blind
    if (action.type === 'bet' && action.amount < state.bigBlind) {
      return { valid: false, error: `Minimum bet is ${state.bigBlind}` }
    }

    // Minimum raise is the big blind
    if (action.type === 'raise') {
      const otherPlayers = state.players.filter((p) => p.id !== playerId)
      const blindsPosted = state.boardState === 'deal' && state.players.some((p) => p.currentBet > 0)
      const highestBet = Math.max(blindsPosted ? state.bigBlind : 0, ...otherPlayers.map((p) => p.currentBet))
      const betToCall = highestBet - player.currentBet
      const minRaise = betToCall + state.bigBlind

      if (action.amount < minRaise && action.amount < player.chips) {
        return { valid: false, error: `Minimum raise is ${minRaise}` }
      }
    }
  }

  // Validate call amount
  if (action.type === 'call') {
    const otherPlayers = state.players.filter((p) => p.id !== playerId)
    const blindsPosted = state.boardState === 'deal' && state.players.some((p) => p.currentBet > 0)
    const highestBet = Math.max(blindsPosted ? state.bigBlind : 0, ...otherPlayers.map((p) => p.currentBet))
    const betToCall = highestBet - player.currentBet

    if (player.chips < betToCall) {
      return { valid: false, error: `Insufficient chips to call (have ${player.chips}, need ${betToCall})` }
    }
  }

  return { valid: true }
}
